import i18n from '../i18n/config';
import { useTranslation } from 'react-i18next';
import React, { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Container,
    Typography,
    Grid,
    Card,
    CardContent,
    CardMedia,
    Button,
    Chip,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    TextField,
    InputAdornment,
    Paper,
    Skeleton,
    Alert,
    Divider,
    FormGroup,
    FormControlLabel,
    Checkbox,
    IconButton,
    Drawer,
    useMediaQuery,
    useTheme,
    Stack,
    alpha
} from '@mui/material';
import {
    Search as SearchIcon,
    FilterList as FilterIcon,
    Store as StoreIcon,
    LocalOffer as OfferIcon,
    Clear as ClearIcon,
    TuneOutlined as TuneIcon,
    Close as CloseIcon,
    Category as CategoryIcon,
    ChevronLeft as ChevronLeftIcon,
    ChevronRight as ChevronRightIcon,
    AddShoppingCart as AddShoppingCartIcon,
} from '@mui/icons-material';

import ProductCard from '../components/store/ProductCard';
import { ecommerceApi, cartHelpers } from '../services/ecommerceApi';
import { useCart } from '../hooks/useCart';
import { useNotification } from '../components/common/GlobalNotifications';
import { useNavigate } from 'react-router-dom';

const PAGE_SIZE = 12;

const sortItems = (items, sortBy) => {
    const list = [...items];
    switch (sortBy) {
        case 'price_asc':
            return list.sort((a, b) => Number(a.price || 0) - Number(b.price || 0));
        case 'price_desc':
            return list.sort((a, b) => Number(b.price || 0) - Number(a.price || 0));
        case 'name_asc':
            return list.sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')));
        case 'featured':
            return list.sort((a, b) => Number(Boolean(b.is_featured)) - Number(Boolean(a.is_featured)));
        case 'stock_desc':
            return list.sort((a, b) => Number(b.stock_quantity || 0) - Number(a.stock_quantity || 0));
        case 'discount_desc':
            return list.sort((a, b) => Number(b.discount_percentage || 0) - Number(a.discount_percentage || 0));
        case 'newest':
        default:
            return list.sort((a, b) => new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime());
    }
};

const buildAvailability = (item) => {
    if (item.type === 'kit') {
        const missing = Number(item.missing_components || 0);
        if (missing > 0) return 'with_shortage';
        return 'available_now';
    }

    const qty = Number(item.stock_quantity || 0);
    if (qty > 0) return 'available_now';
    if (item.allow_backorder || (item.restock_eta_days || 0) > 0) return 'backorder';
    return 'with_shortage';
};

const KitCard = ({ kit, onClick, onAdd, t }) => (
    <Card
        sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            '&:hover': { transform: 'translateY(-4px)', boxShadow: 4 }
        }}
        onClick={() => onClick(kit)}
    >
        <Box sx={{ position: 'relative' }}>
            <CardMedia
                component="img"
                height="220"
                image={kit.main_image || '/images/kit-placeholder.jpg'}
                alt={kit.name}
            />
            <Chip label="KIT" color="secondary" size="small" sx={{ position: 'absolute', top: 10, left: 10, fontWeight: 600 }} />
            {kit.is_featured && (
                <Chip label={t('hardcoded.components_store_productcard.destacado')} color="primary" size="small" sx={{ position: 'absolute', top: 10, right: 10, fontWeight: 600 }} />
            )}
        </Box>
        <CardContent sx={{ flexGrow: 1 }}>
            <Typography variant="h6" fontWeight={600} gutterBottom>{kit.name}</Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
                SKU: {kit.sku || t('hardcoded.pages_storepage.no_disponible')}
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
                {kit.total_items || kit.products?.length || 0} {i18n.t('hardcoded.pages_storepage.productos_incluidos')}</Typography>
            <Typography variant="h6" color="primary" fontWeight={700}>
                {cartHelpers.formatCurrency(kit.price)}
            </Typography>
        </CardContent>
        <Box sx={{ p: 2, pt: 0 }}>
            <Button
                fullWidth
                variant="contained"
                startIcon={<AddShoppingCartIcon />}
                onClick={(e) => {
                    e.stopPropagation();
                    onAdd(kit);
                }}
            >
                {t('hardcoded.components_store_productcard.agregar_al_carrito')}</Button>
        </Box>
    </Card>
);

const StorePage = () => {
  const { t } = useTranslation();
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const { addToCart, openCartDrawer } = useCart();
    const { showError, showSuccess } = useNotification();

    const [catalog, setCatalog] = useState([]);
    const [featuredItems, setFeaturedItems] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchLoading, setSearchLoading] = useState(false);
    const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

    const [filters, setFilters] = useState({
        searchName: '',
        searchSku: '',
        categoryId: '',
        subcategoryId: '',
        itemType: 'all',
        featuredOnly: false,
        stockMode: 'all', // all|in_stock|out_of_stock
        availability: 'all', // all|available_now|with_shortage|backorder
        minPrice: '',
        maxPrice: '',
        sortBy: t('hardcoded.pages_storepage.newest'),
    });

    const [page, setPage] = useState(1);

    useEffect(() => {
        void loadInitialData();
    }, []);

    const loadInitialData = async () => {
        setLoading(true);
        try {
            const [categoriesRes, featuredProductsRes, featuredKitsRes, productsRes, kitsRes] = await Promise.all([
                ecommerceApi.categories.getAll(),
                ecommerceApi.products.getFeatured(6),
                ecommerceApi.kits.getFeatured(4),
                ecommerceApi.products.getAll({ page: 1, per_page: 100, is_active: true }),
                ecommerceApi.kits.getAll({ page: 1, per_page: 100, is_active: true }),
            ]);

            setCategories(categoriesRes.data || []);

            const featured = [
                ...(featuredProductsRes.data || []).map((item) => ({ ...item, type: 'product' })),
                ...(featuredKitsRes.data || []).map((item) => ({ ...item, type: 'kit' })),
            ];
            setFeaturedItems(featured);

            const normalizedProducts = (productsRes.data?.items || []).map((p) => ({
                ...p,
                type: 'product',
                availability_status: buildAvailability(p),
            }));

            const normalizedKits = (kitsRes.data?.items || []).map((k) => ({
                ...k,
                type: 'kit',
                availability_status: buildAvailability(k),
                stock_quantity: Number(k.stock_quantity || 0),
            }));

            setCatalog([...normalizedProducts, ...normalizedKits]);
        } catch (error) {
            showError(t('hardcoded.pages_storepage.error_al_cargar_la_tienda'));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        setSearchLoading(true);
        const t = setTimeout(() => {
            setPage(1);
            setSearchLoading(false);
        }, 220);
        return () => clearTimeout(t);
    }, [filters]);

    const filteredItems = useMemo(() => {
        const nameQuery = String(filters.searchName || '').trim().toLowerCase();
        const skuQuery = String(filters.searchSku || '').trim().toLowerCase();
        const minPrice = filters.minPrice === '' ? null : Number(filters.minPrice);
        const maxPrice = filters.maxPrice === '' ? null : Number(filters.maxPrice);

        let items = catalog.filter((item) => {
            if (nameQuery && !String(item.name || '').toLowerCase().includes(nameQuery)) return false;
            if (skuQuery && !String(item.sku || '').toLowerCase().includes(skuQuery)) return false;
            if (filters.subcategoryId && item.type === 'product' && String(item.category_id) !== String(filters.subcategoryId)) return false;
            if (!filters.subcategoryId && filters.categoryId && item.type === 'product') {
                const allowedCategoryIds = [
                    String(filters.categoryId),
                    ...categories.filter((c) => String(c.parent_id || '') === String(filters.categoryId)).map((c) => String(c.id)),
                ];
                if (!allowedCategoryIds.includes(String(item.category_id))) return false;
            }
            if (filters.itemType !== 'all' && item.type !== filters.itemType) return false;
            if (filters.featuredOnly && !item.is_featured) return false;

            const price = Number(item.price || 0);
            if (minPrice !== null && price < minPrice) return false;
            if (maxPrice !== null && price > maxPrice) return false;

            if (filters.stockMode === 'in_stock') {
                if (item.type === 'product' && !(Number(item.stock_quantity || 0) > 0 || !item.track_inventory)) return false;
                if (item.type === 'kit' && Number(item.stock_quantity || 0) <= 0 && !item.is_active) return false;
            }
            if (filters.stockMode === 'out_of_stock') {
                if (item.type === 'product' && (Number(item.stock_quantity || 0) > 0 || !item.track_inventory)) return false;
                if (item.type === 'kit' && Number(item.stock_quantity || 0) > 0) return false;
            }

            if (filters.availability !== 'all' && item.availability_status !== filters.availability) return false;

            return true;
        });

        return sortItems(items, filters.sortBy);
    }, [catalog, filters, categories]);

    const totalPages = Math.max(1, Math.ceil(filteredItems.length / PAGE_SIZE));
    const pagedItems = filteredItems.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

    useEffect(() => {
        if (page > totalPages) setPage(totalPages);
    }, [page, totalPages]);

    const handleItemClick = (item) => {
        if (!item?.id) {
            showError(t('hardcoded.pages_storepage.item_invalido'));
            return;
        }
        navigate(item.type === 'kit' ? `/store/kit/${item.id}` : `/store/product/${item.id}`);
    };

    const clearFilters = () => {
        setFilters({
            searchName: '',
            searchSku: '',
            categoryId: '',
            subcategoryId: '',
            itemType: 'all',
            featuredOnly: false,
            stockMode: 'all',
            availability: 'all',
            minPrice: '',
            maxPrice: '',
            sortBy: t('hardcoded.pages_storepage.newest'),
        });
        setPage(1);
    };

    const hasActiveFilters = Object.entries(filters).some(([key, value]) => {
        if (key === 'itemType') return value !== 'all';
        if (key === 'stockMode') return value !== 'all';
        if (key === 'availability') return value !== 'all';
        if (key === 'sortBy') return value !== 'newest';
        if (typeof value === 'boolean') return value;
        return String(value || '').trim() !== '';
    });

    const addKitToCart = async (kit) => {
        try {
            await addToCart({ kit_id: kit.id, quantity: 1 });
            showSuccess(t('hardcoded.pages_kitdetailpage.kit_name_agregado_al_carrito', { name: kit.name }));
            openCartDrawer();
        } catch {
            showError(t('hardcoded.pages_storepage.no_se_pudo_agregar_el'));
        }
    };

    const FiltersSidebar = () => (
        <Box sx={{ position: 'sticky', top: 20, maxHeight: 'calc(100vh - 100px)', overflowY: 'auto' }}>
            <Paper elevation={2} sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TuneIcon color="primary" />
                        <Typography variant="h6" fontWeight={600}>{i18n.t('hardcoded.pages_storepage.filtros')}</Typography>
                    </Box>
                    <Stack direction="row" spacing={1}>
                        {hasActiveFilters && (
                            <Button size="small" startIcon={<ClearIcon />} onClick={clearFilters} sx={{ textTransform: 'none' }}>
                                {i18n.t('common.clear')}</Button>
                        )}
                        {!isMobile && (
                            <IconButton size="small" onClick={() => setSidebarCollapsed(true)}>
                                <ChevronLeftIcon />
                            </IconButton>
                        )}
                    </Stack>
                </Box>

                <Divider sx={{ mb: 2 }} />

                <TextField
                    fullWidth
                    size="small"
                    label={t('hardcoded.pages_storepage.buscar_por_nombre')}
                    value={filters.searchName}
                    onChange={(e) => setFilters((prev) => ({ ...prev, searchName: e.target.value }))}
                    sx={{ mb: 2 }}
                    InputProps={{
                        startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
                    }}
                />

                <TextField
                    fullWidth
                    size="small"
                    label={t('hardcoded.pages_storepage.buscar_por_sku')}
                    value={filters.searchSku}
                    onChange={(e) => setFilters((prev) => ({ ...prev, searchSku: e.target.value }))}
                    sx={{ mb: 2 }}
                />

                <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                    <InputLabel>{i18n.t('hardcoded.pages_storepage.categoria')}</InputLabel>
                    <Select
                        value={filters.categoryId}
                        label={t('hardcoded.pages_storepage.categoria')}
                        onChange={(e) => setFilters((prev) => ({ ...prev, categoryId: e.target.value, subcategoryId: '' }))}
                    >
                        <MenuItem value="">{i18n.t('hardcoded.components_settings_notificationpreferenceseditor.todas')}</MenuItem>
                        {categories.map((category) => (
                            <MenuItem key={category.id} value={category.id}>{category.name}</MenuItem>
                        ))}
                    </Select>
                </FormControl>

                {!!filters.categoryId && categories.some((c) => String(c.parent_id || '') === String(filters.categoryId)) && (
                    <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                        <InputLabel>{i18n.t('hardcoded.pages_storepage.subcategoria')}</InputLabel>
                        <Select
                            value={filters.subcategoryId}
                            label={t('hardcoded.pages_storepage.subcategoria')}
                            onChange={(e) => setFilters((prev) => ({ ...prev, subcategoryId: e.target.value }))}
                        >
                            <MenuItem value="">{i18n.t('hardcoded.components_settings_notificationpreferenceseditor.todas')}</MenuItem>
                            {categories
                                .filter((c) => String(c.parent_id || '') === String(filters.categoryId))
                                .map((subcategory) => (
                                    <MenuItem key={subcategory.id} value={subcategory.id}>{subcategory.name}</MenuItem>
                                ))}
                        </Select>
                    </FormControl>
                )}

                <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                    <InputLabel>{i18n.t('vehicles.form.type')}</InputLabel>
                    <Select
                        value={filters.itemType}
                        label={t('vehicles.form.type')}
                        onChange={(e) => setFilters((prev) => ({ ...prev, itemType: e.target.value }))}
                    >
                        <MenuItem value="all">{i18n.t('hardcoded.pages_storepage.productos_y_kits')}</MenuItem>
                        <MenuItem value="product">{t('hardcoded.pages_storepage.solo_productos')}</MenuItem>
                        <MenuItem value="kit">{t('hardcoded.pages_storepage.solo_kits')}</MenuItem>
                    </Select>
                </FormControl>

                <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                    <InputLabel>{t('hardcoded.pages_storepage.disponibilidad_stock')}</InputLabel>
                    <Select
                        value={filters.stockMode}
                        label={t('hardcoded.pages_storepage.disponibilidad_stock')}
                        onChange={(e) => setFilters((prev) => ({ ...prev, stockMode: e.target.value }))}
                    >
                        <MenuItem value="all">{i18n.t('hardcoded.components_alarmprofiles_assignmentstable.todos')}</MenuItem>
                        <MenuItem value="in_stock">{t('hardcoded.pages_storepage.solo_en_stock')}</MenuItem>
                        <MenuItem value="out_of_stock">{t('hardcoded.pages_storepage.solo_agotados')}</MenuItem>
                    </Select>
                </FormControl>

                <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                    <InputLabel>{t('hardcoded.pages_storepage.disponibilidad_estimada')}</InputLabel>
                    <Select
                        value={filters.availability}
                        label={t('hardcoded.pages_storepage.disponibilidad_estimada')}
                        onChange={(e) => setFilters((prev) => ({ ...prev, availability: e.target.value }))}
                    >
                        <MenuItem value="all">{i18n.t('hardcoded.components_settings_notificationpreferenceseditor.todas')}</MenuItem>
                        <MenuItem value="available_now">{i18n.t('hardcoded.pages_storepage.disponible_ahora')}</MenuItem>
                        <MenuItem value="with_shortage">{t('hardcoded.pages_storepage.con_faltantes')}</MenuItem>
                        <MenuItem value="backorder">{t('hardcoded.pages_storepage.sobre_pedido_llegada_posterior')}</MenuItem>
                    </Select>
                </FormControl>

                <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                    <TextField
                        size="small"
                        label="Precio min"
                        type="number"
                        fullWidth
                        value={filters.minPrice}
                        onChange={(e) => setFilters((prev) => ({ ...prev, minPrice: e.target.value }))}
                    />
                    <TextField
                        size="small"
                        label="Precio max"
                        type="number"
                        fullWidth
                        value={filters.maxPrice}
                        onChange={(e) => setFilters((prev) => ({ ...prev, maxPrice: e.target.value }))}
                    />
                </Stack>

                <FormControlLabel
                    control={
                        <Checkbox
                            size="small"
                            checked={filters.featuredOnly}
                            onChange={(e) => setFilters((prev) => ({ ...prev, featuredOnly: e.target.checked }))}
                        />
                    }
                    label={t('hardcoded.pages_storepage.solo_destacados')}
                    sx={{ mb: 1 }}
                />

                <FormControl fullWidth size="small">
                    <InputLabel>{t('hardcoded.pages_storepage.ordenar_por')}</InputLabel>
                    <Select
                        value={filters.sortBy}
                        label={t('hardcoded.pages_storepage.ordenar_por')}
                        onChange={(e) => setFilters((prev) => ({ ...prev, sortBy: e.target.value }))}
                    >
                        <MenuItem value="newest">{t('hardcoded.pages_storepage.mas_recientes')}</MenuItem>
                        <MenuItem value="price_asc">{i18n.t('hardcoded.pages_storepage.precio_menor_a_mayor')}</MenuItem>
                        <MenuItem value="price_desc">{i18n.t('hardcoded.pages_storepage.precio_mayor_a_menor')}</MenuItem>
                        <MenuItem value="name_asc">Nombre A-Z</MenuItem>
                        <MenuItem value="featured">{i18n.t('hardcoded.pages_storepage.destacados')}</MenuItem>
                        <MenuItem value="stock_desc">{t('hardcoded.pages_storepage.stock_disponible')}</MenuItem>
                        <MenuItem value="discount_desc">{i18n.t('hardcoded.pages_storepage.mayor_descuento')}</MenuItem>
                    </Select>
                </FormControl>
            </Paper>
        </Box>
    );

    return (
        <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
            <Container maxWidth="xl" sx={{ py: 3 }}>
                <Box
                    sx={{
                        mb: 4,
                        p: 3,
                        borderRadius: 2,
                        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.10)} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)`
                    }}
                >
                    <Typography variant="h3" fontWeight={700} color="primary" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <StoreIcon sx={{ fontSize: 48 }} />
                        {i18n.t('hardcoded.pages_storepage.tienda_arsenal')}</Typography>
                    <Typography variant="h6" color="text.secondary">
                        {t('hardcoded.pages_storepage.equipos_y_sensores_tpms_de')}</Typography>
                </Box>

                <Grid container spacing={3}>
                    {!isMobile && !sidebarCollapsed && (
                        <Grid item xs={12} md={3}>
                            <FiltersSidebar />
                        </Grid>
                    )}

                    {!isMobile && sidebarCollapsed && (
                        <Grid item xs="auto" sx={{ display: 'flex', alignItems: 'flex-start' }}>
                            <Button
                                variant="outlined"
                                onClick={() => setSidebarCollapsed(false)}
                                startIcon={<ChevronRightIcon />}
                                sx={{ mt: 1.5, minWidth: 64 }}
                            >
                                {i18n.t('hardcoded.pages_storepage.filtros')}</Button>
                        </Grid>
                    )}

                    <Grid item xs={12} md={sidebarCollapsed ? 11 : 9}>
                        {isMobile && (
                            <Box sx={{ mb: 2 }}>
                                <Button
                                    fullWidth
                                    variant="outlined"
                                    startIcon={<FilterIcon />}
                                    onClick={() => setFilterDrawerOpen(true)}
                                    sx={{ py: 1.5 }}
                                >
                                    {i18n.t('hardcoded.pages_storepage.filtros')}</Button>
                            </Box>
                        )}

                        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="h6" fontWeight={600}>
                                {filteredItems.length} {i18n.t('hardcoded.pages_storepage.resultados')}</Typography>
                            <Typography variant="body2" color="text.secondary">
                                {i18n.t('hardcoded.pages_storepage.pagina')}{page} de {totalPages}
                            </Typography>
                        </Box>

                        {!hasActiveFilters && featuredItems.length > 0 && (
                            <Box sx={{ mb: 4 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                    <OfferIcon color="primary" />
                                    <Typography variant="h5" fontWeight={600}>{i18n.t('hardcoded.pages_storepage.destacados')}</Typography>
                                </Box>
                                <Grid container spacing={2}>
                                    {featuredItems.slice(0, 4).map((item) => (
                                        <Grid item xs={12} sm={6} lg={3} key={`${item.type}-${item.id}`}>
                                            {item.type === 'kit'
                                                ? <KitCard kit={item} onClick={handleItemClick} onAdd={addKitToCart} t={t} />
                                                : <ProductCard product={item} onProductClick={handleItemClick} showWishlist={false} />}
                                        </Grid>
                                    ))}
                                </Grid>
                                <Divider sx={{ my: 4 }} />
                            </Box>
                        )}

                        <Box>
                            {loading || searchLoading ? (
                                <Grid container spacing={2}>
                                    {Array.from({ length: 8 }).map((_, index) => (
                                        <Grid item xs={12} sm={6} lg={4} key={index}>
                                            <Card>
                                                <Skeleton variant="rectangular" height={240} />
                                                <CardContent>
                                                    <Skeleton variant="text" height={30} />
                                                    <Skeleton variant="text" height={20} width="60%" />
                                                    <Skeleton variant="text" height={25} width="40%" />
                                                </CardContent>
                                            </Card>
                                        </Grid>
                                    ))}
                                </Grid>
                            ) : pagedItems.length > 0 ? (
                                <>
                                    <Grid container spacing={2}>
                                        {pagedItems.map((item) => (
                                            <Grid item xs={12} sm={6} lg={4} key={`${item.type}-${item.id}`}>
                                                {item.type === 'kit'
                                                    ? <KitCard kit={item} onClick={handleItemClick} onAdd={addKitToCart} t={t} />
                                                    : <ProductCard product={item} onProductClick={handleItemClick} showWishlist={false} />}
                                            </Grid>
                                        ))}
                                    </Grid>

                                    {totalPages > 1 && (
                                        <Stack direction="row" spacing={2} justifyContent="center" alignItems="center" sx={{ mt: 4 }}>
                                            <Button variant="outlined" disabled={page <= 1} onClick={() => setPage((prev) => prev - 1)}>
                                                {i18n.t('hardcoded.components_tutorial_tutorialtooltip.anterior')}</Button>
                                            <Typography variant="body2" color="text.secondary">{i18n.t('hardcoded.pages_storepage.pagina')}{page} de {totalPages}</Typography>
                                            <Button variant="outlined" disabled={page >= totalPages} onClick={() => setPage((prev) => prev + 1)}>
                                                {i18n.t('hardcoded.components_auth_register.siguiente')}</Button>
                                        </Stack>
                                    )}
                                </>
                            ) : (
                                <Alert severity="info" sx={{ mt: 2 }}>
                                    {t('hardcoded.pages_storepage.no_hay_productos_o_kits')}</Alert>
                            )}
                        </Box>
                    </Grid>
                </Grid>
            </Container>

            {isMobile && (
                <Drawer anchor="left" open={filterDrawerOpen} onClose={() => setFilterDrawerOpen(false)}>
                    <Box sx={{ width: 310, p: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography variant="h6" fontWeight={600}>{i18n.t('hardcoded.pages_storepage.filtros')}</Typography>
                            <IconButton onClick={() => setFilterDrawerOpen(false)}>
                                <CloseIcon />
                            </IconButton>
                        </Box>
                        <FiltersSidebar />
                    </Box>
                </Drawer>
            )}
        </Box>
    );
};

export default StorePage;
